"use client";
import React from "react";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";
import ServiceDetailSheet, {
  parseServiceDescription,
  SheetService,
} from "components/shop/ServiceDetailSheet";
import cx from "classnames";
import { BookingQRModal } from "components/common";
import { formatPrice } from "utils/currencyFormatter";
import { get } from "utils/lodash";
import { useT } from "providers/I18nProvider";

/**
 * "Bảng giá dịch vụ" của shop.
 *
 * Trước đây file này rỗng và không được render, nên khách xem trang shop không
 * biết shop làm những dịch vụ gì, giá bao nhiêu — trong khi dữ liệu đã có sẵn
 * trong CMS (shop Vyan Home Spa có 205 dịch vụ).
 *
 * Cách trình bày: nhóm theo danh mục con (Chăm da, Massage, Gội đầu…), mỗi nhóm
 * mở/đóng được; nhóm đầu mở sẵn. Trong mỗi nhóm chỉ hiện 6 dịch vụ, còn lại bấm
 * "Xem thêm" — để một shop 200 dịch vụ không kéo dài vô tận.
 */
const PAGE_SIZE = 6;
/** Mobile: mỗi tab hiện 4 dịch vụ + "Xem thêm" */
const MOBILE_PAGE = 4;

interface ServiceItem extends SheetService {
  stepCount: number;
}
interface ServiceGroup {
  name: string;
  items: ServiceItem[];
}

/** subShopCategory có thể là object hoặc mảng tuỳ dữ liệu → lấy tên nhóm đầu tiên */
const groupNameOf = (svc: any, fallback: string): string => {
  const sc = get(svc, "subShopCategory");
  if (Array.isArray(sc)) return get(sc, "0.name") || fallback;
  return get(sc, "name") || fallback;
};

/**
 * Tách logic nhóm dịch vụ thành hàm thuần (dùng chung cho useState initializer
 * và useMemo render) để tính được "nhóm đầu tiên sau khi sort" NGAY từ lần vẽ
 * đầu — tránh phải mở nhóm qua useEffect (chạy sau mount) gây layout shift.
 */
const buildGroups = (services: any[] | undefined, otherLabel: string): ServiceGroup[] => {
  const map = new Map<string, ServiceItem[]>();
  for (const svc of services || []) {
    const name = get(svc, "name");
    if (!name) continue;
    const key = groupNameOf(svc, otherLabel);
    if (!map.has(key)) map.set(key, []);
    const description = get(svc, "description");
    map.get(key)!.push({
      id: get(svc, "id"),
      name,
      menuPrice: Number(get(svc, "menuPrice")) || 0,
      duration: Number(get(svc, "duration")) || 0,
      isHighlight: !!get(svc, "isHighlight"),
      group: key,
      description,
      stepCount: parseServiceDescription(description).steps.length,
    });
  }
  return Array.from(map.entries())
    .map(([name, items]) => ({
      name,
      items: [...items].sort((a, b) => Number(!!b.isHighlight) - Number(!!a.isHighlight)),
    }))
    .sort((a, b) => b.items.length - a.items.length);
};

const ShopServiceMenu: React.FC<{
  services?: any[];
  shop?: any;
  offers?: any[];
}> = ({ services, shop, offers = [] }) => {
  const t = useT();
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    // Khởi tạo thẳng trong useState(() => …) bằng cùng logic nhóm (buildGroups)
    // dùng ở useMemo dưới đây, thay vì mở qua useEffect sau mount — useEffect
    // chạy sau lần vẽ đầu nên nhóm đầu tiên đóng rồi mới mở ra, gây layout
    // shift (CLS) ngay khi trang load.
    () => {
      const initialGroups = buildGroups(services, t("shopServices.otherGroup"));
      return initialGroups.length ? { [initialGroups[0].name]: true } : {};
    },
  );
  const [shownCount, setShownCount] = React.useState<Record<string, number>>({});
  const [qrOpen, setQrOpen] = React.useState(false);

  // Danh sách dịch vụ do trang lấy sẵn ở máy chủ (nằm trong HTML, Google đọc được).
  const groups = React.useMemo<ServiceGroup[]>(
    () => buildGroups(services, t("shopServices.otherGroup")),
    [services], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ---- Mobile: tab chip + bottom sheet chi tiết ----
  const [activeTab, setActiveTab] = React.useState(0);
  const [mobileShown, setMobileShown] = React.useState(MOBILE_PAGE);
  const [detail, setDetail] = React.useState<ServiceItem | null>(null);
  // Ưu đãi của shop (lấy sẵn ở server, dùng chung với ShopOffers) — khớp theo campaignPackage.service.id
  const openDetail = (item: ServiceItem) => setDetail(item);

  if (!groups.length) return null;

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const tab = groups[Math.min(activeTab, groups.length - 1)];
  const tabItems = tab.items.slice(0, mobileShown);
  const tabRemaining = tab.items.length - tabItems.length;
  const detailSiblings = detail
    ? groups.find((g) => g.name === detail.group)?.items || []
    : [];

  return (
    <>
      {/* ===== Mobile: tab chip cuộn ngang + dòng dịch vụ bấm mở sheet ===== */}
      <section className="md:hidden bg-white py-4">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="text-[17px] font-extrabold text-black01 !mb-0">
            {t("shopServices.shortTitle")}
          </h2>
          <span className="text-xs text-gray-500">
            {t("shopServices.count", { count: total })}
          </span>
        </div>
        {groups.length > 1 && (
          <div className="-mx-4 mb-1 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide" role="tablist">
            {groups.map((g, i) => {
              const on = g.name === tab.name;
              return (
                <button
                  key={g.name}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => {
                    setActiveTab(i);
                    setMobileShown(MOBILE_PAGE);
                  }}
                  className={cx(
                    "shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors",
                    on
                      ? "border-primary bg-primary text-white"
                      : "border-gray-200 bg-white text-gray-600",
                  )}
                >
                  {g.name} ({g.items.length})
                </button>
              );
            })}
          </div>
        )}
        <div className="divide-y divide-gray-100">
          {tabItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openDetail(item)}
              className="flex w-full items-center justify-between gap-3 py-3 text-left"
            >
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold leading-snug text-black01">
                  {item.name}
                  {item.isHighlight ? (
                    <span className="ml-1.5 inline-block rounded-full bg-orange-50 px-1.5 py-px align-middle text-[11px] font-bold text-orange-500">
                      {t("shopServices.highlight")}
                    </span>
                  ) : null}
                </span>
                {item.duration || item.stepCount ? (
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {[
                      item.duration ? t("shopServices.minutes", { count: item.duration }) : "",
                      item.stepCount ? t("shopServices.steps", { count: item.stepCount }) : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                ) : null}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {item.menuPrice ? (
                  <span className="text-[14px] font-bold text-primary">
                    {formatPrice(item.menuPrice)}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">{t("shopServices.contactPrice")}</span>
                )}
                <ChevronRight size={16} className="text-gray-300" />
              </span>
            </button>
          ))}
        </div>
        {tabRemaining > 0 ? (
          <button
            type="button"
            onClick={() => setMobileShown((n) => n + MOBILE_PAGE * 3)}
            className="mt-1 w-full rounded-xl bg-gray-50 py-2.5 text-[13px] font-bold text-primary"
          >
            {t("shopServices.showMore", { count: tabRemaining })}
          </button>
        ) : mobileShown > MOBILE_PAGE && tab.items.length > MOBILE_PAGE ? (
          <button
            type="button"
            onClick={() => setMobileShown(MOBILE_PAGE)}
            className="mt-1 w-full rounded-xl bg-gray-50 py-2.5 text-[13px] font-bold text-gray-500"
          >
            {t("shopServices.showLess")}
          </button>
        ) : null}
      </section>

      <ServiceDetailSheet
        open={!!detail}
        service={detail}
        siblings={detailSiblings}
        shop={shop}
        offers={offers}
        onClose={() => setDetail(null)}
        onSelect={(s) => setDetail(s as ServiceItem)}
        onBook={() => {
          setDetail(null);
          setQrOpen(true);
        }}
      />

      {/* ===== Desktop: giữ accordion như cũ ===== */}
      <section className="hidden md:block bg-white">
        <div>
          <div className="mb-5">
            <h2 className="text-xl md:text-2xl font-extrabold text-black01 !mb-1">
              {t("shopServices.title")}
            </h2>
            <p className="text-sm text-gray-600 !mb-0">
              {t("shopServices.desc", { count: total })}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {groups.map((group) => {
              const open = !!openGroups[group.name];
              const shown = shownCount[group.name] || PAGE_SIZE;
              const visible = group.items.slice(0, shown);
              const remaining = group.items.length - visible.length;
              return (
                <div
                  key={group.name}
                  className="rounded-2xl border border-gray-100 overflow-hidden"
                >
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() =>
                      setOpenGroups((s) => ({ ...s, [group.name]: !open }))
                    }
                    className="w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className="font-bold text-black01">
                      {group.name}
                      <span className="ml-2 text-sm font-medium text-gray-500">
                        ({group.items.length})
                      </span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={cx(
                        "text-gray-400 transition-transform shrink-0",
                        open && "rotate-180",
                      )}
                    />
                  </button>

                  {open ? (
                    <div className="divide-y divide-gray-100">
                      {visible.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => openDetail(item)}
                          className="group/svc flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-primary/5"
                        >
                          <div className="min-w-0">
                            <p className="!mb-0 text-[15px] text-black01 font-medium group-hover/svc:text-primary">
                              {item.name}
                            </p>
                            {item.duration ? (
                              <p className="!mb-0 mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500">
                                <Clock size={12} />
                                {t("shopServices.minutes", {
                                  count: item.duration,
                                })}
                              </p>
                            ) : null}
                          </div>
                          <div className="shrink-0 text-right">
                            {item.menuPrice ? (
                              <span className="text-[15px] font-bold text-primary">
                                {formatPrice(item.menuPrice)}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">
                                {t("shopServices.contactPrice")}
                              </span>
                            )}
                            <span className="mt-0.5 block text-xs text-gray-400 group-hover/svc:text-primary">
                              {t("shopServices.viewDetail")}
                            </span>
                          </div>
                        </button>
                      ))}

                      {remaining > 0 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setShownCount((s) => ({
                              ...s,
                              [group.name]: shown + PAGE_SIZE * 2,
                            }))
                          }
                          className="w-full px-4 py-3 text-sm font-bold text-primary hover:bg-primary/5 transition-colors"
                        >
                          {t("shopServices.showMore", { count: remaining })}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="rounded-full bg-primary px-7 py-3 text-sm font-bold text-white shadow-sm transition-transform hover:scale-[1.02] active:scale-95"
            >
              {t("shopServices.bookOnApp")}
            </button>
          </div>
        </div>
      </section>

      <BookingQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </>
  );
};

export default ShopServiceMenu;
